import { env } from './env'

export function getOrgSlug(): string {
  const parts = window.location.hostname.split(/\.(?:admin|app)\./)
  return parts.length > 1 ? parts[0]! : ''
}

/**
 * Host (with port, when the current origin carries one) of the parent domain
 * the admin app is served from. On an org subdomain the leading label is
 * stripped; on the bare domain the host is already parent.
 */
export function bareAdminHost(): string {
  const host = window.location.host
  return getOrgSlug() === '' ? host : host.replace(/^[^.]+\./, '')
}

/**
 * Absolute URL on the bare admin domain, inheriting the current scheme and
 * port. Cross-subdomain hops MUST be built this way: a hard-coded `https://`
 * plus a `hostname`-derived host silently drops the port and upgrades the
 * scheme, which is fatal off the deployed origin — local dev serves http on
 * :5174, and the resulting `https://<bare-host>/…` resolves to nothing.
 */
export function bareAdminUrl(path: string): string {
  return `${window.location.protocol}//${bareAdminHost()}${path}`
}

/**
 * Organization slugs reach the client over the network (JWT claims, /admin/me,
 * assume-org) and are interpolated into the URL *authority*, where `/` and `@`
 * relocate the host: a slug of `evil.com/` turns
 * `https://<slug>.admin.example.com/x` into a URL whose host is `evil.com`.
 * Only a bare DNS label may pass. Same shape `organizations.orgSlug` is
 * validated against in OrgSettingsUpdateSchema and in the backend's
 * lib/bidderUrl builder — keep the three in step.
 */
const ORG_SLUG = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/

export function isOrgSlug(value: string | null | undefined): value is string {
  return typeof value === 'string' && ORG_SLUG.test(value)
}

/**
 * Absolute URL on an org's admin subdomain, same scheme/port rules as above.
 * Throws on anything that is not a bare DNS label — there is no safe URL to
 * return, and emitting an authority the caller does not fully control is the
 * entire risk. A caller holding a slug off the wire gates on isOrgSlug and
 * renders on the current origin instead of hopping.
 */
export function orgAdminUrl(slug: string, path: string): string {
  if (!isOrgSlug(slug)) {
    throw new Error(`orgAdminUrl: refusing a non-slug authority: ${JSON.stringify(slug)}`)
  }
  return `${window.location.protocol}//${slug}.${bareAdminHost()}${path}`
}

/**
 * Public bidder site of one auction on its org's subdomain: the bidder twin of
 * orgAdminUrl. The admin host's `admin.` label becomes `app.` and the port is
 * the bidder app's own (from env.bidderBaseUrl, empty on the deployed origin),
 * the same derivation bidderBaseUrl uses. Same refusal as orgAdminUrl for an
 * org slug that is not a bare DNS label.
 */
export function orgBidderEventUrl(orgSlug: string, eventSlug: string): string {
  if (!isOrgSlug(orgSlug)) {
    throw new Error(`orgBidderEventUrl: refusing a non-slug authority: ${JSON.stringify(orgSlug)}`)
  }
  const { protocol, hostname } = window.location
  const bareHostname = getOrgSlug() === '' ? hostname : hostname.replace(/^[^.]+\./, '')
  const bidderHostname = bareHostname.replace(/^admin\./, 'app.')
  const bidderPort = new URL(env.bidderBaseUrl).port
  const portSuffix = bidderPort ? `:${bidderPort}` : ''
  return `${protocol}//${orgSlug}.${bidderHostname}${portSuffix}/${encodeURIComponent(eventSlug)}`
}

/**
 * Parent domain shared by the bare admin host and every org subdomain — the
 * host from the `admin.`/`app.` label onward, port excluded. Empty when the
 * current host has no such label (plain `localhost` dev), where there are no
 * sibling subdomains to share anything with.
 */
export function adminParentDomain(): string {
  const match = window.location.hostname.match(/(?:^|\.)((?:admin|app)\..+)$/)
  return match ? match[1]! : ''
}
